import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import InlineFeedback from '../components/InlineFeedback';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) return envBase.trim().replace(/\/+$/, '');

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const currentHost = window.location.hostname;
    if (currentHost === 'localhost' || currentHost === '127.0.0.1' || currentHost === '::1' || currentHost === '[::1]') {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${currentHost}:8000/api`;
  }

  return 'http://127.0.0.1:8000/api';
};

const API_BASE_URL = resolveApiBaseUrl();
const apiUrl = (path = '') => `${API_BASE_URL}/${String(path).replace(/^\/+/, '')}`;
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
const INPUT_CLASS = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm shadow-sm transition focus:border-[#6D3B7E] focus:outline-none focus:ring-2 focus:ring-[#6D3B7E]/20';
const buildAuthHeaders = (accessToken) => ({ Authorization: `Bearer ${accessToken}` });

const isInvalidTokenError = (error) => (
  error?.response?.status === 401
  || error?.response?.data?.code === 'token_not_valid'
);

const createSessionExpiredError = () => {
  const error = new Error('Your session expired. Please log in again.');
  error.response = { data: { error: error.message } };
  return error;
};

const parseJsonArray = (value) => {
  if (!value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return null;
  }
};

const extractApiError = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data?.error === 'string') return data.error;
  if (typeof data?.detail === 'string') return data.detail;

  const findMessage = (value) => {
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.map(findMessage).find(Boolean);
    if (value && typeof value === 'object') return Object.values(value).map(findMessage).find(Boolean);
    return '';
  };

  return findMessage(data) || fallback;
};

const EditorSection = ({ number, title, description, children }) => (
  <section className="rounded-xl border border-[#E8E0EC] bg-white p-4 sm:p-5">
    <div className="mb-4 flex items-start gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F0E5F4] text-sm font-bold text-[#6D3B7E]">{number}</span>
      <div>
        <h3 className="font-semibold text-[#4B2A56]">{title}</h3>
        <p className="mt-0.5 text-xs leading-5 text-gray-500">{description}</p>
      </div>
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

const Field = ({ label, hint, count, children }) => (
  <div>
    <div className="mb-1.5 flex items-end justify-between gap-3">
      <label className="text-sm font-medium text-gray-800">{label}</label>
      {typeof count === 'string' ? <span className="text-xs text-gray-400">{count}</span> : null}
    </div>
    {children}
    {hint ? <p className="mt-1.5 text-xs leading-5 text-gray-500">{hint}</p> : null}
  </div>
);

const OwnerCompanyPageEditor = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [config, setConfig] = useState(null);
  const [versions, setVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingTarget, setUploadingTarget] = useState('');
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [rollbackVersion, setRollbackVersion] = useState('');
  const [savedFormSnapshot, setSavedFormSnapshot] = useState('');
  const [form, setForm] = useState({
    primaryColor: '#6D3B7E',
    accentColor: '#CDA349',
    logoUrl: '',
    tagline: '',
    headline: '',
    subheadline: '',
    primaryCta: 'Book Appointment',
    secondaryCta: 'View Services',
    heroImageUrl: '',
    aboutTitle: '',
    aboutBody: '',
    contactPhone: '',
    contactEmail: '',
    contactAddress: '',
    highlightsJson: '[]',
    testimonialsJson: '[]',
    galleryJson: '[]',
  });

  const clearExpiredSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userRole');
    window.dispatchEvent(new Event('auth-changed'));
    navigate('/login', { replace: true, state: { from: '/owner-company-page' } });
  };

  const authorizedRequest = async (requestFactory) => {
    const activeToken = localStorage.getItem('token');
    try {
      return await requestFactory(activeToken);
    } catch (error) {
      if (!isInvalidTokenError(error)) throw error;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        clearExpiredSession();
        throw createSessionExpiredError();
      }

      let refreshedAccessToken;
      try {
        const refreshResponse = await axios.post(apiUrl('token/refresh/'), { refresh: refreshToken });
        refreshedAccessToken = refreshResponse?.data?.access;
        if (!refreshedAccessToken) throw createSessionExpiredError();

        localStorage.setItem('token', refreshedAccessToken);
        window.dispatchEvent(new Event('auth-changed'));
      } catch (refreshError) {
        clearExpiredSession();
        if (isInvalidTokenError(refreshError) || refreshError?.message === 'Your session expired. Please log in again.') {
          throw createSessionExpiredError();
        }
        throw refreshError;
      }

      try {
        return await requestFactory(refreshedAccessToken);
      } catch (retryError) {
        if (isInvalidTokenError(retryError)) {
          clearExpiredSession();
          throw createSessionExpiredError();
        }
        throw retryError;
      }
    }
  };

  const mapPayloadToForm = (payload = {}) => ({
    primaryColor: payload?.brand?.primary_color || '#6D3B7E',
    accentColor: payload?.brand?.accent_color || '#CDA349',
    logoUrl: payload?.brand?.logo_url || '',
    tagline: payload?.brand?.tagline || '',
    headline: payload?.hero?.headline || '',
    subheadline: payload?.hero?.subheadline || '',
    primaryCta: payload?.hero?.primary_cta_label || 'Book Appointment',
    secondaryCta: payload?.hero?.secondary_cta_label || 'View Services',
    heroImageUrl: payload?.hero?.hero_image_url || '',
    aboutTitle: payload?.about?.title || '',
    aboutBody: payload?.about?.body || '',
    contactPhone: payload?.contact?.phone || '',
    contactEmail: payload?.contact?.email || '',
    contactAddress: payload?.contact?.address_line || '',
    highlightsJson: JSON.stringify(payload?.highlights || [], null, 2),
    testimonialsJson: JSON.stringify(payload?.testimonials || [], null, 2),
    galleryJson: JSON.stringify(payload?.gallery || [], null, 2),
  });

  const buildPayloadFromForm = () => {
    const highlights = parseJsonArray(form.highlightsJson);
    const testimonials = parseJsonArray(form.testimonialsJson);
    const gallery = parseJsonArray(form.galleryJson);
    if (highlights === null || testimonials === null || gallery === null) return null;

    return {
      brand: { primary_color: form.primaryColor, accent_color: form.accentColor, logo_url: form.logoUrl, tagline: form.tagline },
      hero: {
        headline: form.headline,
        subheadline: form.subheadline,
        primary_cta_label: form.primaryCta,
        secondary_cta_label: form.secondaryCta,
        hero_image_url: form.heroImageUrl,
      },
      about: { title: form.aboutTitle, body: form.aboutBody },
      highlights,
      testimonials,
      gallery,
      contact: { phone: form.contactPhone, email: form.contactEmail, address_line: form.contactAddress },
    };
  };

  const loadData = async ({ preserveFeedback = false } = {}) => {
    if (!token) return;
    try {
      setIsLoading(true);
      const [configRes, versionsRes] = await authorizedRequest((accessToken) => Promise.all([
        axios.get(apiUrl('userprofiles/owner/company-page/'), { headers: buildAuthHeaders(accessToken) }),
        axios.get(apiUrl('userprofiles/owner/company-page/versions/'), { headers: buildAuthHeaders(accessToken) }),
      ]));
      const nextForm = mapPayloadToForm(configRes.data?.draft_payload || {});
      setConfig(configRes.data || null);
      setVersions(versionsRes.data || []);
      setForm(nextForm);
      setSavedFormSnapshot(JSON.stringify(nextForm));
      setRollbackVersion('');
      if (!preserveFeedback) setFeedback({ type: '', message: '' });
    } catch (error) {
      setFeedback({ type: 'error', message: extractApiError(error, 'Unable to load company page editor.') });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const hasUnsavedChanges = savedFormSnapshot !== '' && JSON.stringify(form) !== savedFormSnapshot;
  const highlights = parseJsonArray(form.highlightsJson) || [];
  const testimonials = parseJsonArray(form.testimonialsJson) || [];
  const gallery = parseJsonArray(form.galleryJson) || [];

  const setCollection = (field, rows) => setForm((prev) => ({ ...prev, [field]: JSON.stringify(rows, null, 2) }));
  const updateCollectionItem = (field, rows, index, key, value) => {
    setCollection(field, rows.map((row, rowIndex) => (rowIndex === index ? { ...row, [key]: value } : row)));
  };
  const removeCollectionItem = (field, rows, index) => setCollection(field, rows.filter((_, rowIndex) => rowIndex !== index));

  const uploadImageFile = async (file, kind) => {
    if (!file) return '';
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      throw new Error('Choose a PNG, JPEG, or WebP image.');
    }
    if (file.size > 4 * 1024 * 1024) {
      throw new Error('Image must be 4 MB or smaller.');
    }

    const body = new FormData();
    body.append('kind', kind);
    body.append('image', file);
    const response = await authorizedRequest((accessToken) => axios.post(
      apiUrl('company-page-assets/upload/'),
      body,
      { headers: buildAuthHeaders(accessToken) },
    ));
    return response.data?.url || '';
  };

  const handleSingleImageUpload = async (event, kind, field) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setUploadingTarget(kind);
      const url = await uploadImageFile(file, kind);
      setForm((prev) => ({ ...prev, [field]: url }));
      setFeedback({ type: 'success', message: 'Image uploaded. Save your changes when the preview looks right.' });
    } catch (error) {
      setFeedback({ type: 'error', message: extractApiError(error, error.message || 'Unable to upload image.') });
    } finally {
      setUploadingTarget('');
    }
  };

  const handleGalleryUpload = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (!selectedFiles.length) return;

    const availableSlots = 12 - gallery.length;
    const files = selectedFiles.slice(0, availableSlots);
    if (!files.length) {
      setFeedback({ type: 'error', message: 'The gallery already has the maximum of 12 photos.' });
      return;
    }

    try {
      setUploadingTarget('gallery');
      const uploadedRows = [];
      for (const file of files) {
        const url = await uploadImageFile(file, 'gallery');
        uploadedRows.push({ image_url: url, alt: file.name.replace(/\.[^.]+$/, '') });
      }
      setCollection('galleryJson', [...gallery, ...uploadedRows]);
      const skippedCount = selectedFiles.length - files.length;
      setFeedback({
        type: 'success',
        message: `${uploadedRows.length} photo${uploadedRows.length === 1 ? '' : 's'} uploaded.${skippedCount ? ` ${skippedCount} skipped because the gallery limit is 12.` : ''} Save your changes when ready.`,
      });
    } catch (error) {
      setFeedback({ type: 'error', message: extractApiError(error, error.message || 'Unable to upload gallery photos.') });
    } finally {
      setUploadingTarget('');
    }
  };

  const handleSaveDraft = async () => {
    const payload = buildPayloadFromForm();
    if (!payload) {
      setFeedback({ type: 'error', message: 'One of the page sections contains invalid data.' });
      return;
    }
    try {
      setIsSaving(true);
      await authorizedRequest((accessToken) => axios.patch(
        apiUrl('userprofiles/owner/company-page/draft/'),
        { payload },
        { headers: buildAuthHeaders(accessToken) },
      ));
      await loadData({ preserveFeedback: true });
      setFeedback({ type: 'success', message: 'Your draft changes are saved.' });
    } catch (error) {
      setFeedback({ type: 'error', message: extractApiError(error, 'Unable to save draft.') });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (hasUnsavedChanges) {
      setFeedback({ type: 'error', message: 'Save your changes before publishing the page.' });
      return;
    }
    try {
      setIsSaving(true);
      await authorizedRequest((accessToken) => axios.post(
        apiUrl('userprofiles/owner/company-page/publish/'),
        {},
        { headers: buildAuthHeaders(accessToken) },
      ));
      await loadData({ preserveFeedback: true });
      setFeedback({ type: 'success', message: 'Your company page changes are now live.' });
    } catch (error) {
      setFeedback({ type: 'error', message: extractApiError(error, 'Unable to publish the page.') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRollback = async () => {
    if (!rollbackVersion) {
      setFeedback({ type: 'error', message: 'Choose a version to restore.' });
      return;
    }
    const versionToRestore = rollbackVersion;
    try {
      setIsSaving(true);
      await authorizedRequest((accessToken) => axios.post(
        apiUrl('userprofiles/owner/company-page/rollback/'),
        { version: Number(versionToRestore) },
        { headers: buildAuthHeaders(accessToken) },
      ));
      await loadData({ preserveFeedback: true });
      setFeedback({ type: 'success', message: `Version ${versionToRestore} was restored.` });
    } catch (error) {
      setFeedback({ type: 'error', message: extractApiError(error, 'Unable to restore that version.') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    if (!savedFormSnapshot) return;
    setForm(JSON.parse(savedFormSnapshot));
    setFeedback({ type: 'info', message: 'Unsaved changes were discarded.' });
  };

  const previewPayload = buildPayloadFromForm() || {};
  const statusLabel = String(config?.status || 'draft').replaceAll('_', ' ');
  const heroStyle = {
    backgroundColor: form.primaryColor || '#6D3B7E',
    backgroundImage: form.heroImageUrl
      ? `linear-gradient(110deg, rgba(75,42,86,.9), rgba(205,163,73,.72)), url("${form.heroImageUrl}")`
      : `linear-gradient(110deg, ${form.primaryColor || '#6D3B7E'}, ${form.accentColor || '#CDA349'})`,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
  };

  return (
    <div className="min-h-screen bg-[#F6F4F8] px-3 py-4 text-[#2E2E2E] sm:px-4 sm:py-8">
      <div className="mx-auto max-w-7xl space-y-5">
        <header className="rounded-2xl bg-gradient-to-r from-[#4B2A56] to-[#8A4AA3] p-5 text-white shadow-lg sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-purple-200">Website editor</p>
              <h1 className="mt-1 text-2xl font-bold sm:text-3xl">Customize your company page</h1>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-purple-100">
                <span>{config?.company_name || 'Unassigned company'}</span><span aria-hidden="true">•</span>
                <span className="rounded-full bg-white/15 px-2.5 py-1 font-semibold capitalize">{statusLabel}</span>
                <span>Version {config?.version || 1}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => navigate('/owner-dashboard')} className="rounded-lg bg-white/15 px-4 py-2 text-sm font-semibold text-white hover:bg-white/25">Owner dashboard</button>
              {config?.company_slug ? <button type="button" onClick={() => navigate(`/companies/${config.company_slug}`)} className="rounded-lg bg-[#CDA349] px-4 py-2 text-sm font-semibold text-white hover:bg-[#b8913e]">View public page</button> : null}
            </div>
          </div>
        </header>

        {feedback.message ? <InlineFeedback type={feedback.type || 'info'} message={feedback.message} /> : null}
        {isLoading ? <InlineFeedback type="info" message="Loading company page settings..." /> : null}

        <section className="grid items-start gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(360px,2fr)]">
          <div className="space-y-4">
            <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div><h2 className="text-xl font-semibold text-[#4B2A56]">Page content</h2><p className="mt-1 text-sm text-gray-500">Work through each section. Your preview updates as you type.</p></div>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${hasUnsavedChanges ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>{hasUnsavedChanges ? 'Unsaved changes' : 'All changes saved'}</span>
              </div>
            </div>

            <EditorSection number="1" title="Brand style" description="Choose the colors, logo, and short tagline visitors see first.">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Primary color" hint="Used for the main background and headings.">
                  <div className="flex gap-2"><input aria-label="Primary color picker" type="color" className="h-11 w-14 cursor-pointer rounded-lg border border-gray-300 bg-white p-1" value={HEX_COLOR.test(form.primaryColor) ? form.primaryColor : '#6D3B7E'} onChange={(e) => setForm((prev) => ({ ...prev, primaryColor: e.target.value.toUpperCase() }))} /><input aria-label="Primary color" className={INPUT_CLASS} value={form.primaryColor} maxLength={7} onChange={(e) => setForm((prev) => ({ ...prev, primaryColor: e.target.value }))} placeholder="#6D3B7E" /></div>
                </Field>
                <Field label="Accent color" hint="Used for buttons and visual highlights.">
                  <div className="flex gap-2"><input aria-label="Accent color picker" type="color" className="h-11 w-14 cursor-pointer rounded-lg border border-gray-300 bg-white p-1" value={HEX_COLOR.test(form.accentColor) ? form.accentColor : '#CDA349'} onChange={(e) => setForm((prev) => ({ ...prev, accentColor: e.target.value.toUpperCase() }))} /><input aria-label="Accent color" className={INPUT_CLASS} value={form.accentColor} maxLength={7} onChange={(e) => setForm((prev) => ({ ...prev, accentColor: e.target.value }))} placeholder="#CDA349" /></div>
                </Field>
              </div>
              <Field label="Tagline" count={`${form.tagline.length}/140`} hint="A short phrase that describes what makes your company special."><input aria-label="Tagline" className={INPUT_CLASS} maxLength={140} value={form.tagline} onChange={(e) => setForm((prev) => ({ ...prev, tagline: e.target.value }))} placeholder="Professional beauty services tailored to you." /></Field>
              <Field label="Company logo" hint="Upload a PNG, JPEG, or WebP image up to 4 MB. A transparent PNG works best.">
                <div className="space-y-2">
                  <input aria-label="Upload company logo" type="file" accept="image/png,image/jpeg,image/webp" disabled={Boolean(uploadingTarget)} onChange={(event) => handleSingleImageUpload(event, 'logo', 'logoUrl')} className="block w-full rounded-lg border border-dashed border-[#BFAAC8] bg-[#FBF8FC] p-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[#6D3B7E] file:px-3 file:py-2 file:font-semibold file:text-white hover:file:bg-[#593066] disabled:opacity-50" />
                  {uploadingTarget === 'logo' ? <p className="text-xs font-semibold text-[#6D3B7E]">Uploading logo...</p> : null}
                  <input aria-label="Logo URL" type="url" className={INPUT_CLASS} value={form.logoUrl} onChange={(e) => setForm((prev) => ({ ...prev, logoUrl: e.target.value }))} placeholder="Or paste an image URL" />
                </div>
              </Field>
            </EditorSection>

            <EditorSection number="2" title="Hero section" description="This is the large introduction at the top of your public page.">
              <Field label="Main headline" count={`${form.headline.length}/90`}><input aria-label="Headline" className={INPUT_CLASS} maxLength={90} value={form.headline} onChange={(e) => setForm((prev) => ({ ...prev, headline: e.target.value }))} placeholder="Beauty appointments made simple" /></Field>
              <Field label="Supporting text" count={`${form.subheadline.length}/240`}><textarea aria-label="Subheadline" className={INPUT_CLASS} rows={3} maxLength={240} value={form.subheadline} onChange={(e) => setForm((prev) => ({ ...prev, subheadline: e.target.value }))} placeholder="Tell visitors what they can expect." /></Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Main button label" hint="Takes visitors to booking."><input aria-label="Primary CTA" className={INPUT_CLASS} maxLength={40} value={form.primaryCta} onChange={(e) => setForm((prev) => ({ ...prev, primaryCta: e.target.value }))} placeholder="Book Appointment" /></Field>
                <Field label="Second button label" hint="Takes visitors to services."><input aria-label="Secondary CTA" className={INPUT_CLASS} maxLength={40} value={form.secondaryCta} onChange={(e) => setForm((prev) => ({ ...prev, secondaryCta: e.target.value }))} placeholder="View Services" /></Field>
              </div>
              <Field label="Hero background image" hint="Optional. Upload a wide image up to 4 MB with room for readable text.">
                <div className="space-y-2">
                  <input aria-label="Upload hero background image" type="file" accept="image/png,image/jpeg,image/webp" disabled={Boolean(uploadingTarget)} onChange={(event) => handleSingleImageUpload(event, 'hero', 'heroImageUrl')} className="block w-full rounded-lg border border-dashed border-[#BFAAC8] bg-[#FBF8FC] p-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[#6D3B7E] file:px-3 file:py-2 file:font-semibold file:text-white hover:file:bg-[#593066] disabled:opacity-50" />
                  {uploadingTarget === 'hero' ? <p className="text-xs font-semibold text-[#6D3B7E]">Uploading hero image...</p> : null}
                  <input aria-label="Hero image URL" type="url" className={INPUT_CLASS} value={form.heroImageUrl} onChange={(e) => setForm((prev) => ({ ...prev, heroImageUrl: e.target.value }))} placeholder="Or paste an image URL" />
                </div>
              </Field>
            </EditorSection>

            <EditorSection number="3" title="About your company" description="Give customers a quick reason to trust and choose your team.">
              <Field label="Section title" count={`${form.aboutTitle.length}/80`}><input aria-label="About title" className={INPUT_CLASS} maxLength={80} value={form.aboutTitle} onChange={(e) => setForm((prev) => ({ ...prev, aboutTitle: e.target.value }))} placeholder="About Our Company" /></Field>
              <Field label="Company description" count={`${form.aboutBody.length}/1000`} hint="Mention your specialties, experience, service area, or approach."><textarea aria-label="About body" className={INPUT_CLASS} rows={5} maxLength={1000} value={form.aboutBody} onChange={(e) => setForm((prev) => ({ ...prev, aboutBody: e.target.value }))} placeholder="Tell customers about your company." /></Field>
            </EditorSection>

            <EditorSection number="4" title="Service highlights" description="Add up to six short reasons customers should book with you.">
              {highlights.map((row, index) => <div key={`highlight-${index}`} className="rounded-xl border border-gray-200 bg-[#FCFBFD] p-3">
                <div className="mb-3 flex items-center justify-between"><p className="text-sm font-semibold text-[#4B2A56]">Highlight {index + 1}</p><button type="button" onClick={() => removeCollectionItem('highlightsJson', highlights, index)} className="text-xs font-semibold text-red-600 hover:text-red-700" aria-label={`Remove highlight ${index + 1}`}>Remove</button></div>
                <div className="space-y-3"><input aria-label={`Highlight ${index + 1} title`} className={INPUT_CLASS} maxLength={60} value={row.title || ''} onChange={(e) => updateCollectionItem('highlightsJson', highlights, index, 'title', e.target.value)} placeholder="Experienced professionals" /><textarea aria-label={`Highlight ${index + 1} description`} className={INPUT_CLASS} rows={2} maxLength={240} value={row.description || ''} onChange={(e) => updateCollectionItem('highlightsJson', highlights, index, 'description', e.target.value)} placeholder="Describe this benefit in one or two sentences." /></div>
              </div>)}
              {highlights.length === 0 ? <p className="rounded-lg border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">No highlights yet.</p> : null}
              <button type="button" disabled={highlights.length >= 6} onClick={() => setCollection('highlightsJson', [...highlights, { title: '', description: '' }])} className="rounded-lg border border-[#6D3B7E] px-4 py-2 text-sm font-semibold text-[#6D3B7E] hover:bg-[#F8F1FA] disabled:cursor-not-allowed disabled:opacity-50">+ Add highlight</button>
            </EditorSection>

            <EditorSection number="5" title="Testimonials" description="Add up to six customer quotes to build trust.">
              {testimonials.map((row, index) => <div key={`testimonial-${index}`} className="rounded-xl border border-gray-200 bg-[#FCFBFD] p-3">
                <div className="mb-3 flex items-center justify-between"><p className="text-sm font-semibold text-[#4B2A56]">Testimonial {index + 1}</p><button type="button" onClick={() => removeCollectionItem('testimonialsJson', testimonials, index)} className="text-xs font-semibold text-red-600 hover:text-red-700" aria-label={`Remove testimonial ${index + 1}`}>Remove</button></div>
                <div className="space-y-3"><textarea aria-label={`Testimonial ${index + 1} quote`} className={INPUT_CLASS} rows={3} maxLength={280} value={row.quote || ''} onChange={(e) => updateCollectionItem('testimonialsJson', testimonials, index, 'quote', e.target.value)} placeholder="What did your customer say?" /><input aria-label={`Testimonial ${index + 1} author`} className={INPUT_CLASS} maxLength={80} value={row.author || ''} onChange={(e) => updateCollectionItem('testimonialsJson', testimonials, index, 'author', e.target.value)} placeholder="Customer name" /></div>
              </div>)}
              {testimonials.length === 0 ? <p className="rounded-lg border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">No testimonials yet.</p> : null}
              <button type="button" disabled={testimonials.length >= 6} onClick={() => setCollection('testimonialsJson', [...testimonials, { quote: '', author: '' }])} className="rounded-lg border border-[#6D3B7E] px-4 py-2 text-sm font-semibold text-[#6D3B7E] hover:bg-[#F8F1FA] disabled:cursor-not-allowed disabled:opacity-50">+ Add testimonial</button>
            </EditorSection>

            <EditorSection number="6" title="Photo gallery" description="Upload up to twelve photos and add accessible descriptions.">
              <div className="rounded-xl border border-dashed border-[#BFAAC8] bg-[#FBF8FC] p-3">
                <label className="mb-2 block text-sm font-semibold text-[#4B2A56]">Upload gallery photos</label>
                <input aria-label="Upload gallery photos" type="file" accept="image/png,image/jpeg,image/webp" multiple disabled={Boolean(uploadingTarget) || gallery.length >= 12} onChange={handleGalleryUpload} className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[#6D3B7E] file:px-3 file:py-2 file:font-semibold file:text-white hover:file:bg-[#593066] disabled:opacity-50" />
                <p className="mt-2 text-xs text-gray-500">PNG, JPEG, or WebP • 4 MB maximum per photo • {gallery.length}/12 used</p>
                {uploadingTarget === 'gallery' ? <p className="mt-2 text-xs font-semibold text-[#6D3B7E]">Uploading photos...</p> : null}
              </div>
              {gallery.map((row, index) => <div key={`gallery-${index}`} className="rounded-xl border border-gray-200 bg-[#FCFBFD] p-3">
                <div className="mb-3 flex items-center justify-between"><p className="text-sm font-semibold text-[#4B2A56]">Photo {index + 1}</p><button type="button" onClick={() => removeCollectionItem('galleryJson', gallery, index)} className="text-xs font-semibold text-red-600 hover:text-red-700" aria-label={`Remove photo ${index + 1}`}>Remove</button></div>
                <div className="grid gap-3 sm:grid-cols-[96px_1fr]"><div className="flex h-24 items-center justify-center overflow-hidden rounded-lg border bg-gray-100 text-xs text-gray-400">{row.image_url ? <img src={row.image_url} alt="" className="h-full w-full object-cover" /> : 'Preview'}</div><div className="space-y-3"><input aria-label={`Photo ${index + 1} URL`} type="url" className={INPUT_CLASS} value={row.image_url || ''} onChange={(e) => updateCollectionItem('galleryJson', gallery, index, 'image_url', e.target.value)} placeholder="https://example.com/photo.jpg" /><input aria-label={`Photo ${index + 1} description`} className={INPUT_CLASS} maxLength={120} value={row.alt || ''} onChange={(e) => updateCollectionItem('galleryJson', gallery, index, 'alt', e.target.value)} placeholder="Describe the photo for accessibility" /></div></div>
              </div>)}
              {gallery.length === 0 ? <p className="rounded-lg border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">No gallery photos yet.</p> : null}
              <button type="button" disabled={gallery.length >= 12} onClick={() => setCollection('galleryJson', [...gallery, { image_url: '', alt: '' }])} className="rounded-lg border border-[#6D3B7E] px-4 py-2 text-sm font-semibold text-[#6D3B7E] hover:bg-[#F8F1FA] disabled:cursor-not-allowed disabled:opacity-50">+ Add photo</button>
            </EditorSection>

            <EditorSection number="7" title="Contact information" description="Tell visitors how to reach or find your business.">
              <div className="grid gap-4 sm:grid-cols-2"><Field label="Phone number"><input aria-label="Contact phone" type="tel" className={INPUT_CLASS} maxLength={30} value={form.contactPhone} onChange={(e) => setForm((prev) => ({ ...prev, contactPhone: e.target.value }))} placeholder="(555) 123-4567" /></Field><Field label="Email address"><input aria-label="Contact email" type="email" className={INPUT_CLASS} maxLength={120} value={form.contactEmail} onChange={(e) => setForm((prev) => ({ ...prev, contactEmail: e.target.value }))} placeholder="hello@yourcompany.com" /></Field></div>
              <Field label="Business address"><input aria-label="Contact address" className={INPUT_CLASS} maxLength={200} value={form.contactAddress} onChange={(e) => setForm((prev) => ({ ...prev, contactAddress: e.target.value }))} placeholder="123 Main Street, Chicago, IL" /></Field>
            </EditorSection>

            <div className="sticky bottom-3 z-10 rounded-2xl border border-[#DED3E3] bg-white/95 p-4 shadow-xl backdrop-blur">
              <div className="flex flex-wrap items-center justify-between gap-3"><div><p className="text-sm font-semibold text-[#4B2A56]">Ready to go live?</p><p className="text-xs text-gray-500">Save your edits, then publish them directly to your company page.</p></div><div className="flex flex-wrap gap-2">
                {hasUnsavedChanges ? <button type="button" disabled={isSaving || Boolean(uploadingTarget)} onClick={handleDiscardChanges} className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100">Discard</button> : null}
                <button type="button" disabled={isSaving || Boolean(uploadingTarget) || !hasUnsavedChanges} onClick={handleSaveDraft} className="rounded-lg bg-[#304A8A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#263c70] disabled:cursor-not-allowed disabled:opacity-50">{isSaving ? 'Working...' : 'Save changes'}</button>
                <button type="button" disabled={isSaving || Boolean(uploadingTarget) || hasUnsavedChanges} onClick={handlePublish} className="rounded-lg bg-[#2F855A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#276749] disabled:cursor-not-allowed disabled:opacity-50">Publish changes</button>
              </div></div>
            </div>

            <details className="rounded-xl border border-gray-200 bg-white p-4 text-sm"><summary className="cursor-pointer font-semibold text-[#4B2A56]">Version history and restore</summary><p className="mt-3 text-xs leading-5 text-gray-500">Restoring replaces the current public page with a previously published version.</p><div className="mt-3 flex flex-col gap-2 sm:flex-row"><select aria-label="Rollback version" value={rollbackVersion} onChange={(e) => setRollbackVersion(e.target.value)} className={`${INPUT_CLASS} flex-1`}><option value="">Choose a published version</option>{versions.map((versionRow) => <option key={versionRow.id} value={versionRow.version}>Version {versionRow.version} ({versionRow.source})</option>)}</select><button type="button" disabled={isSaving || !rollbackVersion} onClick={handleRollback} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50">Restore version</button></div></details>
          </div>

          <aside className="rounded-2xl bg-white p-4 shadow-sm lg:sticky lg:top-5 sm:p-5">
            <div className="flex items-center justify-between gap-3"><div><h2 className="text-lg font-semibold text-[#4B2A56]">Live preview</h2><p className="text-xs text-gray-500">Updates automatically as you type</p></div><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" aria-hidden="true" /></div>
            <div className="mt-4 overflow-hidden rounded-2xl border border-gray-200 bg-[#FAF8F6]">
              <div className="p-5 text-white" style={heroStyle}>
                {form.logoUrl ? <img src={form.logoUrl} alt="Company logo preview" className="mb-4 max-h-12 max-w-[180px] object-contain" /> : null}
                <p className="text-xs uppercase tracking-wide opacity-80">{previewPayload?.brand?.tagline || 'Your tagline'}</p><h3 className="mt-2 text-2xl font-bold">{previewPayload?.hero?.headline || 'Your headline'}</h3><p className="mt-2 text-sm opacity-90">{previewPayload?.hero?.subheadline || 'Your supporting message will appear here.'}</p>
                <div className="mt-4 flex flex-wrap gap-2"><span className="rounded-full bg-white px-4 py-2 text-xs font-semibold text-[#2E2E2E]">{previewPayload?.hero?.primary_cta_label || 'Book Appointment'}</span><span className="rounded-full bg-white/20 px-4 py-2 text-xs font-semibold text-white">{previewPayload?.hero?.secondary_cta_label || 'View Services'}</span></div>
              </div>
              <div className="space-y-5 p-5 text-sm">
                <div><h4 className="font-semibold text-[#4B2A56]">{previewPayload?.about?.title || 'About your company'}</h4><p className="mt-2 leading-6 text-gray-600">{previewPayload?.about?.body || 'Your company description will appear here.'}</p></div>
                {highlights.length ? <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">{highlights.map((row, index) => <div key={`preview-highlight-${index}`} className="rounded-lg border bg-white p-3"><p className="font-semibold text-[#4B2A56]">{row.title || `Highlight ${index + 1}`}</p><p className="mt-1 text-xs leading-5 text-gray-500">{row.description || 'Description'}</p></div>)}</div> : null}
                {gallery.filter((row) => row.image_url).length ? <div className="grid grid-cols-3 gap-2">{gallery.filter((row) => row.image_url).slice(0, 6).map((row, index) => <img key={`preview-photo-${index}`} src={row.image_url} alt={row.alt || ''} className="aspect-square w-full rounded-lg object-cover" />)}</div> : null}
                {testimonials.length ? <div className="rounded-xl bg-white p-4 shadow-sm"><p className="italic text-gray-600">“{testimonials[0].quote || 'Customer quote'}”</p><p className="mt-2 text-xs font-semibold text-[#4B2A56]">— {testimonials[0].author || 'Customer name'}</p></div> : null}
                <div className="border-t pt-4 text-xs leading-5 text-gray-600"><p className="font-semibold text-[#4B2A56]">Contact</p><p>{previewPayload?.contact?.phone || 'Phone not added'}</p><p>{previewPayload?.contact?.email || 'Email not added'}</p>{previewPayload?.contact?.address_line ? <p>{previewPayload.contact.address_line}</p> : null}</div>
              </div>
            </div>
          </aside>
        </section>
      </div>
    </div>
  );
};

export default OwnerCompanyPageEditor;
